import React from "react";
import {
  Card,
  Typography,
  List,
  ListItem,
  ListItemPrefix,
  Accordion,
  AccordionHeader,
  AccordionBody
} from "@material-tailwind/react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { PowerIcon, UserCircleIcon } from "@heroicons/react/24/solid"; 
import SidebarHeader from './SidebarHeader'; 

export default function Sidebar({
  menuStructure,
  activeReportId,
  isSidebarOpen,
  isKpiOpen,
  toggleSidebar,
  toggleKpi,
  handleReportClick,
  handleLogout,
  userName,
  userRoles = [] 
}) {
  
  const iconContainerClass = !isSidebarOpen ? "mr-0 mx-auto" : "mr-4";
  
  // Formatage des rôles pour l'affichage 
  const displayRoles = userRoles
    .filter(role => role !== "authenticated") 
    .map(role => role.charAt(0).toUpperCase() + role.slice(1)) 
    .join(', ') || "Utilisateur";

  const getItemClass = (isActive) => 
    `${!isSidebarOpen ? "justify-center px-2" : "px-3"} rounded-lg transition-colors ${isActive ? "bg-blue-50 text-blue-600" : "hover:bg-blue-gray-50 text-blue-gray-700"}`;

  return (
    <Card
      className={`
        h-screen bg-white shadow-xl shadow-blue-gray-900/5 transition-all duration-300 ease-in-out border-r border-blue-gray-100 rounded-none flex flex-col
        ${isSidebarOpen ? "w-[18rem]" : "w-[5rem]"} 
      `}
    >
      {/* --- EN-TÊTE --- */}
      <div className="p-2 flex-shrink-0">
         <SidebarHeader isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      </div>

      {/* --- LISTE DES MENUS --- */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar">
        <List className="min-w-[auto] p-2 gap-1 font-medium">
            {menuStructure.map((section, index) => {
            
           
            if (!section.items || section.items.length === 0) return null;

            // SOUS-MENU 
            if (section.isSubmenu) {
                const isOpen = (isSidebarOpen && isKpiOpen); 

                return (
                <Accordion
                    key={index}
                    open={isOpen}
                    icon={
                    <ChevronDownIcon
                        strokeWidth={2.5}
                        className={`mx-auto h-4 w-4 transition-transform ${isKpiOpen ? "rotate-180" : ""} ${!isSidebarOpen ? "hidden" : ""}`}
                    />
                    }
                >
                    <ListItem className={`p-0 ${!isSidebarOpen ? "justify-center" : ""}`} selected={isOpen}>
                    <AccordionHeader
                        onClick={() => isSidebarOpen ? toggleKpi() : toggleSidebar()}
                        className={`border-b-0 p-3 rounded-lg ${isOpen ? "text-blue-600" : ""}`}
                    >
                        <ListItemPrefix className={iconContainerClass}>
                        {section.icon && <section.icon className="h-5 w-5" />}
                        </ListItemPrefix>
                        
                        {isSidebarOpen && (
                        <Typography color="inherit" className="mr-auto font-medium text-sm">
                            {section.name}
                        </Typography>
                        )}
                    </AccordionHeader>
                    </ListItem>
                    
                    <AccordionBody className="py-1">
                    <List className="p-0 gap-1">
                        {section.items.map((item) => (
                        <ListItem
                            key={item.id}
                            className={getItemClass(activeReportId === item.id)}
                            onClick={() => handleReportClick(item.id)}
                        >
                            <ListItemPrefix className={!isSidebarOpen ? "mr-0" : "mr-3"}>
                            <ChevronRightIcon strokeWidth={3} className="h-3 w-5" />
                            </ListItemPrefix>
                            {isSidebarOpen && <span className="text-sm">{item.name}</span>}
                        </ListItem>
                        ))}
                    </List>
                    </AccordionBody>
                </Accordion>
                );
            }

            // TITRES DE SECTION & ITEMS 
            return (
                <div key={index} className="mt-1">
                    {/* Titre de section (ex: BUREAU D'ÉTUDE) seulement si ouvert et non-Home/Compte */}
                    {isSidebarOpen && section.name !== "HOME" && section.name !== "COMPTE" && (
                        <Typography variant="small" color="blue-gray" className="font-bold opacity-50 px-3 mt-4 mb-2 text-[10px] uppercase tracking-wider">
                            {section.name}
                        </Typography>
                    )}

                    {section.items.map((item) => {
                        if (item.id === 'logout') return null; 

                        const isActive = activeReportId === item.id;

                        return (
                        <ListItem
                            key={item.id}
                            onClick={() => handleReportClick(item.id)}
                            className={getItemClass(isActive)}
                            title={!isSidebarOpen ? item.name : ""}
                        >
                            <ListItemPrefix className={iconContainerClass}>
                                {item.icon && <item.icon className={`h-5 w-5 ${isActive ? "text-blue-600" : ""}`} />}
                            </ListItemPrefix>
                            
                            {isSidebarOpen && (
                            <Typography color="inherit" className="mr-auto font-medium text-sm">
                                {item.name}
                            </Typography>
                            )}
                        </ListItem>
                        );
                    })}
                </div>
            );
            })}
        </List>
      </div>

      {/*  PROFIL & DÉCONNEXION */}
      <div className="p-2 border-t border-blue-gray-50 bg-white z-10 flex-shrink-0">
        
        {/* SECTION PROFIL */}
        <div className={`flex items-center gap-3 mb-2 p-2 rounded-lg transition-colors ${!isSidebarOpen ? "justify-center" : "bg-blue-gray-50/50"}`} title={!isSidebarOpen ? userName : ""}>
            <div className="shrink-0 relative">
                 <UserCircleIcon className="h-9 w-9 text-blue-gray-300" />
                
                 <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full bg-green-400 ring-2 ring-white" />
            </div>

            {isSidebarOpen && (
                <div className="overflow-hidden">
                    <Typography color="blue-gray" className="font-bold text-xs truncate">
                        {userName || "Utilisateur"}
                    </Typography>
                    <Typography color="gray" className="font-normal text-[10px] truncate">
                        {displayRoles}
                    </Typography>
                </div>
            )}
        </div>

        {/* BOUTON DÉCONNEXION */}
        <ListItem 
            onClick={handleLogout}
            className={`hover:bg-red-50 hover:text-red-900 focus:bg-red-50 focus:text-red-900 ${!isSidebarOpen ? "justify-center px-2" : "px-3"}`}
            title={!isSidebarOpen ? "Déconnexion" : ""}
        >
            <ListItemPrefix className={iconContainerClass}>
                <PowerIcon className="h-5 w-5 text-red-500" />
            </ListItemPrefix>
            {isSidebarOpen && <span className="font-medium text-sm text-red-700">Déconnexion</span>}
        </ListItem>
      </div>
    </Card>
  );
}